import React, { useState, useCallback, useRef } from 'react';
import {
  BarChart3, FileSpreadsheet, Loader2, AlertCircle, Printer,
  ChevronRight, Tag, Users, Receipt, Building2, CreditCard, TrendingUp, Wallet,
} from 'lucide-react';
import Button from '../../components/UI/Button';
import { sdk } from '../../lib/sdk';
import { getApiBaseUrl } from '../../lib/apiBaseUrl';
import { getErrorMessage } from '../../lib/utils/errorMessage';
import DateRangePicker from './components/DateRangePicker';
import LayoutConfigurator, { LayoutConfig, DEFAULT_LAYOUT } from './components/LayoutConfigurator';
import ReportTable from './components/ReportTable';
import ReportSummary from './components/ReportSummary';
import ReportHeader from './components/ReportHeader';
import { ReportCategoryBackButton } from './ReportCategorySelector';
import {
  useActiveIncomeDepartments,
  useActiveIncomeTypes,
  useExpenseTypes,
  useActiveAccounts,
} from '../../hooks/useQueries';

export type FinancialModule = 'income' | 'expense';

type TabId =
  | 'byDepartment'
  | 'byType'
  | 'matrix'
  | 'transactions'
  | 'byOperator'
  | 'byAccount';

interface TabDef {
  id: TabId;
  label: string;
  description: string;
  icon: React.ReactNode;
  scope: string;
}

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const daysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const INCOME_TABS: TabDef[] = [
  { id: 'byDepartment', label: 'By Department', description: 'Totals per department', icon: <Building2 className="h-4 w-4" />, scope: 'byIncomeDepartment' },
  { id: 'byType', label: 'By Type', description: 'Totals per income type', icon: <Tag className="h-4 w-4" />, scope: 'byIncomeType' },
  { id: 'matrix', label: 'Dept × Type', description: 'Department/type matrix', icon: <TrendingUp className="h-4 w-4" />, scope: 'byIncomeDepartmentType' },
  { id: 'transactions', label: 'Transactions', description: 'Raw income list', icon: <Receipt className="h-4 w-4" />, scope: 'incomeTransactions' },
  { id: 'byOperator', label: 'By Operator', description: 'Operator breakdown', icon: <Users className="h-4 w-4" />, scope: 'byIncomeOperator' },
];

const EXPENSE_TABS: TabDef[] = [
  { id: 'byType', label: 'By Type', description: 'Totals per expense type', icon: <Tag className="h-4 w-4" />, scope: 'byExpenseType' },
  { id: 'byAccount', label: 'By Account', description: 'Totals per account', icon: <CreditCard className="h-4 w-4" />, scope: 'byExpenseAccount' },
  { id: 'transactions', label: 'Transactions', description: 'Raw expense list', icon: <Receipt className="h-4 w-4" />, scope: 'expenseTransactions' },
  { id: 'byOperator', label: 'By Operator', description: 'Operator breakdown', icon: <Users className="h-4 w-4" />, scope: 'byExpenseOperator' },
];

const TabButton: React.FC<{
  active: boolean; icon: React.ReactNode; label: string; description: string; onClick: () => void;
}> = ({ active, icon, label, description, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border-2 transition-all text-left w-full ${
      active
        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 shadow-md'
        : 'border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-700'
    }`}
  >
    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
      active ? 'bg-primary-500 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-500'
    }`}>{icon}</div>
    <div className="min-w-0 flex-1">
      <p className={`text-xs font-semibold ${active ? 'text-primary-700 dark:text-primary-300' : 'text-gray-700 dark:text-gray-200'}`}>{label}</p>
      <p className="text-[10px] truncate text-gray-400">{description}</p>
    </div>
    {active && <ChevronRight className="h-3.5 w-3.5 text-primary-500 flex-shrink-0" />}
  </button>
);

const MultiSelect: React.FC<{
  label: string;
  options: { id: string; name: string }[];
  selected: string[];
  onChange: (ids: string[]) => void;
}> = ({ label, options, selected, onChange }) => {
  const toggle = (id: string) => {
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  };
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">{label}</label>
      <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
        {options.length === 0 && <span className="text-xs text-gray-400">No options</span>}
        {options.map((o) => (
          <button
            key={o.id}
            type="button"
            onClick={() => toggle(o.id)}
            className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
              selected.includes(o.id)
                ? 'bg-primary-100 border-primary-400 text-primary-800 dark:bg-primary-900/40 dark:text-primary-200'
                : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400'
            }`}
          >
            {o.name}
          </button>
        ))}
      </div>
    </div>
  );
};

interface FinancialReportsPanelProps {
  module: FinancialModule;
  onBack?: () => void;
}

const FinancialReportsPanel: React.FC<FinancialReportsPanelProps> = ({ module, onBack }) => {
  const tabs = module === 'income' ? INCOME_TABS : EXPENSE_TABS;
  const printRef = useRef<HTMLDivElement>(null);
  const title = module === 'income' ? 'Income Reports' : 'Expense Reports';
  const HeaderIcon = module === 'income' ? TrendingUp : Wallet;

  const { data: departmentsRaw = [] } = useActiveIncomeDepartments();
  const { data: incomeTypesRaw = [] } = useActiveIncomeTypes();
  const { data: expenseTypesRaw = [] } = useExpenseTypes();
  const { data: accountsRaw = [] } = useActiveAccounts();

  const departments = (Array.isArray(departmentsRaw) ? departmentsRaw : []).map((d: any) => ({
    id: String(d._id), name: d.name,
  }));
  const incomeTypes = (Array.isArray(incomeTypesRaw) ? incomeTypesRaw : [])
    .filter((t: any) => t.isActive !== false)
    .map((t: any) => ({ id: String(t._id), name: t.name }));
  const expenseTypes = (Array.isArray(expenseTypesRaw) ? expenseTypesRaw : [])
    .filter((t: any) => t.isActive !== false)
    .map((t: any) => ({ id: String(t._id), name: t.name }));
  const accounts = (Array.isArray(accountsRaw) ? accountsRaw : []).map((a: any) => ({
    id: String(a._id), name: a.name || a.accountNumber,
  }));

  const [activeTab, setActiveTab] = useState<TabId>(tabs[0].id);
  const [layout, setLayout] = useState<LayoutConfig>(DEFAULT_LAYOUT);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reportData, setReportData] = useState<any>(null);

  const [startDate, setStartDate] = useState(daysAgo(30));
  const [endDate, setEndDate] = useState(today());
  const [departmentIds, setDepartmentIds] = useState<string[]>([]);
  const [incomeTypeIds, setIncomeTypeIds] = useState<string[]>([]);
  const [expenseTypeIds, setExpenseTypeIds] = useState<string[]>([]);
  const [accountIds, setAccountIds] = useState<string[]>([]);
  const [txnLimit, setTxnLimit] = useState(200);
  const [txnPage, setTxnPage] = useState(1);

  const activeScope = tabs.find((t) => t.id === activeTab)?.scope || tabs[0].scope;

  const buildParams = useCallback((): Record<string, string> => {
    const p: Record<string, string> = { startDate, endDate, scope: activeScope };
    if (module === 'income') {
      if (departmentIds.length) p.departmentIds = departmentIds.join(',');
      if (incomeTypeIds.length) p.incomeTypeIds = incomeTypeIds.join(',');
    } else if (expenseTypeIds.length) {
      p.expenseTypeIds = expenseTypeIds.join(',');
    }
    if (accountIds.length) p.accountIds = accountIds.join(',');
    if (activeTab === 'transactions') {
      p.limit = String(txnLimit);
      p.page = String(txnPage);
    }
    return p;
  }, [startDate, endDate, activeScope, module, departmentIds, incomeTypeIds, expenseTypeIds, accountIds, activeTab, txnLimit, txnPage]);

  const handleGenerate = async () => {
    setError(null);
    if (!startDate || !endDate) { setError('Please select a date range'); return; }
    setLoading(true);
    setReportData(null);
    try {
      const qs = new URLSearchParams(buildParams()).toString();
      const response = await sdk.client.get(`/api/v1/reports/advanced/generate?${qs}`);
      if (response.success) setReportData(response.data);
      else setError(response.message || 'Failed to generate report');
    } catch (e: any) {
      setError(getErrorMessage(e, 'Generation failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format: 'pdf' | 'excel') => {
    if (!reportData) { setError('Generate a report first'); return; }
    setExporting(true);
    try {
      const token = window.localStorage.getItem('hria-token');
      const API = getApiBaseUrl();
      const body = { ...buildParams(), format, layoutConfig: layout };
      const resp = await fetch(`${API}/api/v1/reports/advanced/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!resp.ok) {
        const e = await resp.json().catch(() => ({ message: 'Export failed' }));
        throw new Error(e.message);
      }
      const blob = await resp.blob();
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${module}_report_${activeTab}_${today()}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(getErrorMessage(e, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const handlePrint = () => {
    const el = printRef.current;
    if (!el) return;
    const win = window.open('', '_blank', 'width=900,height=700');
    if (!win) { alert('Please allow popups to print.'); return; }
    win.document.write(`<!DOCTYPE html><html><head><title>Report</title>
      <style>body{font-family:Arial,sans-serif;padding:16px}table{border-collapse:collapse;width:100%}
      th,td{border:1px solid #ccc;padding:4px 8px}th{background:#1a5276;color:#fff}</style></head><body>`);
    win.document.write(el.innerHTML);
    win.document.write('</body></html>');
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 400);
  };

  const renderPreview = () => {
    if (!reportData) return null;
    const rt = reportData.reportType;
    const hdr = reportData.header || {};
    const dateRange = hdr.dateRange || { from: startDate, to: endDate };

    const headerBlock = (
      <ReportHeader
        reportTitle={hdr.reportTitle || title}
        reportSubtitle={hdr.reportSubtitle}
        dateRange={dateRange}
        donationTypes={[]}
        colorScheme={layout.colorScheme}
      />
    );

    const summaryItems: { label: string; value: string | number }[] = [];
    const s = reportData.summary || {};
    if (s.grandTotal !== undefined) summaryItems.push({ label: 'Grand Total', value: `Rs. ${Number(s.grandTotal).toLocaleString('en-PK')}` });
    if (s.totalTransactions !== undefined) summaryItems.push({ label: 'Transactions', value: s.totalTransactions });
    if (s.totalDepartments !== undefined) summaryItems.push({ label: 'Departments', value: s.totalDepartments });
    if (s.totalTypes !== undefined) summaryItems.push({ label: 'Types', value: s.totalTypes });
    if (s.totalAccounts !== undefined) summaryItems.push({ label: 'Accounts', value: s.totalAccounts });

    if (reportData.sections?.length) {
      return (
        <div>
          {headerBlock}
          {summaryItems.length > 0 && <ReportSummary items={summaryItems} />}
          {reportData.sections.map((sec: any, idx: number) => {
            const sectionTitle = sec.departmentName || sec.operatorName || `Section ${idx + 1}`;
            const cols = module === 'income'
              ? [{ label: 'Income Type', key: 'incomeTypeName' }, { label: 'Count', key: 'count', align: 'right' as const }, { label: 'Amount', key: 'totalAmount', isCurrency: true, align: 'right' as const }]
              : [{ label: 'Expense Type', key: 'expenseTypeName' }, { label: 'Count', key: 'count', align: 'right' as const }, { label: 'Amount', key: 'totalAmount', isCurrency: true, align: 'right' as const }];
            return (
              <ReportTable key={idx} sectionTitle={`${sectionTitle} — Rs. ${Number(sec.sectionTotal || 0).toLocaleString('en-PK')}`} columns={cols} rows={sec.rows || []} showTotals colorScheme={layout.colorScheme} />
            );
          })}
        </div>
      );
    }

    let columns: { label: string; key: string; isCurrency?: boolean; align?: 'left' | 'center' | 'right' }[] = [];
    if (rt === 'byIncomeDepartment') {
      columns = [{ label: 'Department', key: 'departmentName' }, { label: 'Count', key: 'count', align: 'right' }, { label: 'Amount', key: 'totalAmount', isCurrency: true, align: 'right' }];
    } else if (rt === 'byIncomeType' || rt === 'byExpenseType') {
      columns = [{ label: 'Type', key: rt === 'byIncomeType' ? 'incomeTypeName' : 'expenseTypeName' }, { label: 'Count', key: 'count', align: 'right' }, { label: 'Amount', key: 'totalAmount', isCurrency: true, align: 'right' }];
    } else if (rt === 'byExpenseAccount') {
      columns = [{ label: 'Account', key: 'accountName' }, { label: 'Count', key: 'count', align: 'right' }, { label: 'Amount', key: 'totalAmount', isCurrency: true, align: 'right' }];
    } else if (rt === 'incomeTransactions') {
      columns = [
        { label: 'Receipt', key: 'receiptId' }, { label: 'Date', key: 'dateStr' },
        { label: 'Department', key: 'departmentName' }, { label: 'Type', key: 'incomeTypeName' },
        { label: 'Account', key: 'accountName' }, { label: 'Operator', key: 'operatorName' },
        { label: 'Amount', key: 'amount', isCurrency: true, align: 'right' },
      ];
    } else if (rt === 'expenseTransactions') {
      columns = [
        { label: 'ID', key: 'expenseId' }, { label: 'Date', key: 'dateStr' },
        { label: 'Description', key: 'name' }, { label: 'Type', key: 'expenseTypeName' },
        { label: 'Account', key: 'accountName' }, { label: 'By', key: 'userName' },
        { label: 'Amount', key: 'amount', isCurrency: true, align: 'right' },
      ];
    }

    return (
      <div>
        {headerBlock}
        {summaryItems.length > 0 && <ReportSummary items={summaryItems} />}
        {columns.length > 0 && (
          <ReportTable columns={columns} rows={reportData.rows || []} showTotals colorScheme={layout.colorScheme} />
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6 pb-10">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center shadow-lg">
            <HeaderIcon className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{title}</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {module === 'income' ? 'Department, type, operator, and transaction reports' : 'Type, account, operator, and transaction reports'}
            </p>
          </div>
        </div>
        {onBack && <ReportCategoryBackButton onBack={onBack} />}
      </div>

      <div className="card border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
          <div className="lg:col-span-3 p-4 border-b lg:border-b-0 lg:border-r border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/30 space-y-2">
            {tabs.map((tab) => (
              <TabButton
                key={tab.id}
                active={activeTab === tab.id}
                icon={tab.icon}
                label={tab.label}
                description={tab.description}
                onClick={() => { setActiveTab(tab.id); setReportData(null); setError(null); }}
              />
            ))}
          </div>

          <div className="lg:col-span-9 p-5 space-y-5">
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={setStartDate}
              onEndDateChange={setEndDate}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {module === 'income' && (
                <>
                  <MultiSelect label="Departments (optional)" options={departments} selected={departmentIds} onChange={setDepartmentIds} />
                  <MultiSelect label="Income types (optional)" options={incomeTypes} selected={incomeTypeIds} onChange={setIncomeTypeIds} />
                </>
              )}
              {module === 'expense' && (
                <MultiSelect label="Expense types (optional)" options={expenseTypes} selected={expenseTypeIds} onChange={setExpenseTypeIds} />
              )}
              <MultiSelect label="Accounts (optional)" options={accounts} selected={accountIds} onChange={setAccountIds} />
            </div>

            {activeTab === 'transactions' && (
              <div className="flex gap-4 items-end">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Limit</label>
                  <input type="number" min={1} max={5000} value={txnLimit} onChange={(e) => setTxnLimit(Number(e.target.value))} className="px-3 py-2 border rounded-lg w-24 dark:bg-gray-800 dark:border-gray-600" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Page</label>
                  <input type="number" min={1} value={txnPage} onChange={(e) => setTxnPage(Number(e.target.value))} className="px-3 py-2 border rounded-lg w-24 dark:bg-gray-800 dark:border-gray-600" />
                </div>
              </div>
            )}

            <LayoutConfigurator value={layout} onChange={setLayout} />

            {error && (
              <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-sm">
                <AlertCircle className="h-4 w-4 flex-shrink-0" /> {error}
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <Button onClick={handleGenerate} disabled={loading}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <BarChart3 className="h-4 w-4 mr-2" />}
                Generate
              </Button>
              <Button variant="secondary" onClick={() => handleExport('excel')} disabled={exporting || !reportData}>
                <FileSpreadsheet className="h-4 w-4 mr-2" /> Excel
              </Button>
              <Button variant="secondary" onClick={() => handleExport('pdf')} disabled={exporting || !reportData}>
                PDF
              </Button>
              <Button variant="secondary" onClick={handlePrint} disabled={!reportData}>
                <Printer className="h-4 w-4 mr-2" /> Print
              </Button>
            </div>
          </div>
        </div>
      </div>

      {reportData && (
        <div className="card overflow-hidden">
          <div ref={printRef} className="p-6">{renderPreview()}</div>
        </div>
      )}
    </div>
  );
};

export default FinancialReportsPanel;
